import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Loader2, Target, Building2, MapPin, Clock, Repeat, TrendingUp,
  ChevronDown, ChevronUp, Users, Search, Briefcase, ShieldCheck,
  Heart, Scale, Truck, Calculator, Info, Sparkles, X, ArrowRight,
  Database, Zap, Filter, Globe, AlertTriangle, CheckCircle2, Eye,
  Layers, Map,
} from 'lucide-react';
import { IntelligenceLayout, fetchWithAuth, MetricCard } from './IntelligenceLayout';
import { WatchlistButton } from '../components/WatchlistButton';
import { CompanyActionMenu } from '../components/CompanyActionMenu';
import { QuickOutreachModal } from '../components/pipelines/QuickOutreachModal';
import CanonicalPreview from '../components/CanonicalPreview';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const NICHES = [
  { key: 'all', label: 'All Industries', icon: Target, color: 'slate' },
  { key: 'accounting_finance', label: 'Accounting & Finance', icon: Calculator, color: 'emerald' },
  { key: 'healthcare', label: 'Healthcare', icon: Heart, color: 'blue' },
  { key: 'legal', label: 'Legal', icon: Scale, color: 'violet' },
  { key: 'logistics', label: 'Logistics & Supply Chain', icon: Truck, color: 'orange' },
];

const TAG_STYLES = {
  target: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  mid_market_fit: 'bg-blue-50 text-blue-700 border-blue-200',
  growth_stage: 'bg-sky-50 text-sky-700 border-sky-200',
  size_unknown: 'bg-gray-100 text-gray-500 border-gray-200',
  manual_override: 'bg-violet-50 text-violet-700 border-violet-200',
  strategic_enterprise: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  enterprise: 'bg-slate-100 text-slate-600 border-slate-200',
  oversized: 'bg-slate-100 text-slate-600 border-slate-200',
  hard_to_access: 'bg-rose-50 text-rose-600 border-rose-200',
  long_term_opportunity: 'bg-cyan-50 text-cyan-700 border-cyan-200',
};
const TAG_LABELS = {
  target: 'Target', mid_market_fit: 'Mid-Market', growth_stage: 'Growth',
  size_unknown: 'Size Unknown', manual_override: 'Manual Override',
  strategic_enterprise: 'Strategic Enterprise', enterprise: 'Enterprise',
  oversized: 'Oversized', hard_to_access: 'Hard to Access',
  long_term_opportunity: 'Long-Term Opportunity',
};

const QualTag = ({ tag }) => (
  <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold border ${TAG_STYLES[tag] || 'bg-gray-100 text-gray-600 border-gray-200'}`}
    data-testid={`tag-${tag}`}>
    {TAG_LABELS[tag] || tag}
  </span>
);

const NicheScoreBadge = ({ score }) => {
  let color = 'bg-gray-100 text-gray-600 border-gray-200';
  if (score >= 8) color = 'bg-emerald-50 text-emerald-700 border-emerald-200';
  else if (score >= 5) color = 'bg-amber-50 text-amber-700 border-amber-200';
  else if (score >= 3) color = 'bg-orange-50 text-orange-600 border-orange-200';
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-sm font-bold border ${color}`} data-testid="niche-score-badge">
      {score}/10
    </span>
  );
};

const EnrichmentBadge = ({ ep }) => {
  if (!ep?.flagged) return null;
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold bg-amber-50 text-amber-700 border border-amber-200" data-testid="enrichment-priority-badge"
      title={ep.reasons?.join(', ')}>
      <Database className="h-3 w-3" /> Needs Enrichment
    </span>
  );
};

/* ── Firmographic Confidence Badge ── */
const ConfidenceBadge = ({ confidence, flags }) => {
  if (!confidence || confidence === 'high') return null;
  const isLow = confidence === 'low';
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold border ${
        isLow
          ? 'bg-red-50 text-red-700 border-red-200'
          : 'bg-amber-50 text-amber-700 border-amber-200'
      }`}
      data-testid={`confidence-${confidence}`}
      title={flags?.join(' | ') || `Firmographic confidence: ${confidence}`}
    >
      <AlertTriangle className="h-3 w-3" />
      {isLow ? 'Low Confidence' : 'Verify Data'}
    </span>
  );
};

/* ── Niche Lane Indicator Badge ── */
const NicheLaneBadge = ({ inNiche, nicheHits }) => {
  if (inNiche) {
    const labels = (nicheHits || []).map(h => h.label).slice(0, 2).join(', ');
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold bg-blue-50 text-blue-700 border border-blue-200"
        data-testid="in-niche-badge" title={`In niche: ${labels}`}>
        <Layers className="h-3 w-3" /> {labels || 'In Lane'}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold bg-slate-50 text-slate-500 border border-slate-200"
      data-testid="broad-target-badge">
      <Globe className="h-3 w-3" /> Broad Target
    </span>
  );
};

const TIER_STYLES = {
  best_immediate: { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700', label: 'Best Immediate', icon: Zap },
  needs_enrichment: { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', label: 'Needs Enrichment', icon: Database },
  strategic_enterprise: { bg: 'bg-indigo-50', border: 'border-indigo-200', text: 'text-indigo-700', label: 'Strategic Enterprise', icon: Building2 },
  broader_qualified: { bg: 'bg-gray-50', border: 'border-gray-200', text: 'text-gray-600', label: 'Broader Qualified', icon: Globe },
};

const TierBadge = ({ tier }) => {
  const s = TIER_STYLES[tier] || TIER_STYLES.broader_qualified;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold ${s.bg} ${s.text} border ${s.border}`} data-testid={`tier-${tier}`}>
      {s.label}
    </span>
  );
};

/* ── Similar Companies Panel ── */
const SimilarPanel = ({ company, niche, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetchWithAuth(`${API_URL}/api/intelligence/niche-lanes/${niche}/similar/${encodeURIComponent(company)}?limit=15`)
      .then(d => setData(d))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [company, niche]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" data-testid="similar-panel-overlay">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col" data-testid="similar-panel">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div>
            <h3 className="text-lg font-bold text-[#0A1F44]">Find More Like This</h3>
            <p className="text-sm text-gray-500">Companies similar to <span className="font-semibold">{company}</span></p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 transition-colors" data-testid="similar-panel-close">
            <X className="h-5 w-5 text-gray-400" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {loading ? (
            <div className="flex items-center justify-center py-12 gap-2 text-gray-400">
              <Loader2 className="h-5 w-5 animate-spin" /> Finding similar companies...
            </div>
          ) : data?.error ? (
            <p className="text-center py-12 text-gray-400">{data.error}</p>
          ) : (
            <>
              <div className="flex items-center gap-4 mb-4 text-xs text-gray-500">
                <span>Ref industry: <strong>{data?.reference_industry || '—'}</strong></span>
                <span>Ref size: <strong>{data?.reference_employee_count ? data.reference_employee_count.toLocaleString() : '—'}</strong></span>
                <span>Found: <strong>{data?.total_found || 0}</strong></span>
              </div>
              <div className="space-y-2">
                {(data?.similar_companies || []).map((c, i) => (
                  <div key={c.company} className="flex items-start gap-3 p-3 rounded-lg border border-gray-100 hover:border-gray-200 hover:bg-gray-50/50 transition-all" data-testid={`similar-row-${i}`}>
                    <div className="h-8 w-8 rounded-lg bg-[#0A1F44]/5 flex items-center justify-center shrink-0 text-sm font-bold text-[#0A1F44]/60">
                      {c.similarity_score}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-[#0A1F44] text-sm">{c.company}</span>
                        <div className="flex gap-1">
                          {c.tags?.slice(0, 3).map(t => <QualTag key={t} tag={t} />)}
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {c.similarity_reasons?.map((r, j) => (
                          <span key={j} className="text-[10px] bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded border border-blue-100">{r}</span>
                        ))}
                      </div>
                      <div className="flex items-center gap-3 mt-1 text-[10px] text-gray-400">
                        <span>{c.total_postings} roles</span>
                        <span>{c.industry || '—'}</span>
                        {c.employee_count > 0 && <span>{c.employee_count.toLocaleString()} emp</span>}
                        {c.states?.length > 0 && <span>{c.states.slice(0, 3).join(', ')}</span>}
                      </div>
                    </div>
                  </div>
                ))}
                {(data?.similar_companies || []).length === 0 && (
                  <p className="text-center py-8 text-gray-400 text-sm">No similar companies found matching qualification criteria.</p>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

/* ── Account Expansion Panel ── */
const AccountPanel = ({ company, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetchWithAuth(`${API_URL}/api/intelligence/account-expansion/${encodeURIComponent(company)}`)
      .then(d => setData(d))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [company]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" data-testid="account-panel-overlay">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col" data-testid="account-panel">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div>
            <h3 className="text-lg font-bold text-[#0A1F44]">Account Expansion</h3>
            <p className="text-sm text-gray-500">{company}</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 transition-colors" data-testid="account-panel-close">
            <X className="h-5 w-5 text-gray-400" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {loading ? (
            <div className="flex items-center justify-center py-12 gap-2 text-gray-400">
              <Loader2 className="h-5 w-5 animate-spin" /> Loading account intelligence...
            </div>
          ) : data?.error ? (
            <p className="text-center py-12 text-gray-400">{data.error}</p>
          ) : (
            <div className="space-y-6">
              <div className="grid grid-cols-4 gap-3">
                <div className="bg-[#0A1F44]/5 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-[#0A1F44]">{data?.total_active_roles || 0}</p>
                  <p className="text-[10px] text-gray-500 uppercase tracking-wider">Active Roles</p>
                </div>
                <div className="bg-[#0A1F44]/5 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-[#0A1F44]">{data?.hiring_velocity_30d || 0}</p>
                  <p className="text-[10px] text-gray-500 uppercase tracking-wider">30d Velocity</p>
                </div>
                <div className="bg-[#0A1F44]/5 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-[#0A1F44]">{data?.aged_roles_count || 0}</p>
                  <p className="text-[10px] text-gray-500 uppercase tracking-wider">Aged 30d+</p>
                </div>
                <div className="bg-[#0A1F44]/5 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-[#0A1F44]">{data?.employee_count ? data.employee_count.toLocaleString() : '—'}</p>
                  <p className="text-[10px] text-gray-500 uppercase tracking-wider">Employees</p>
                </div>
              </div>
              {data?.qualification && (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500">Status:</span>
                  <span className={`px-2 py-0.5 rounded text-xs font-semibold ${data.qualification.qualification_status === 'qualified' ? 'bg-emerald-50 text-emerald-700' : data.qualification.qualification_status === 'manual_override' ? 'bg-violet-50 text-violet-700' : 'bg-red-50 text-red-700'}`}>
                    {data.qualification.qualification_status === 'manual_override' ? 'Manual Override' : data.qualification.qualification_status}
                  </span>
                  {data.qualification.tags?.map(t => <QualTag key={t} tag={t} />)}
                </div>
              )}
              <div>
                <h4 className="text-sm font-semibold text-[#0A1F44] mb-3 flex items-center gap-2">
                  <Briefcase className="h-4 w-4 text-gray-400" /> Role Clusters
                </h4>
                <div className="grid grid-cols-2 gap-2">
                  {(data?.role_clusters || []).map((rc, i) => (
                    <div key={i} className="border border-gray-100 rounded-lg p-3" data-testid={`role-cluster-${i}`}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium text-[#0A1F44]">{rc.cluster}</span>
                        <span className="text-xs font-bold bg-[#0A1F44]/10 text-[#0A1F44] px-2 py-0.5 rounded">{rc.count}</span>
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {rc.sample_titles?.slice(0, 3).map((t, j) => (
                          <span key={j} className="text-[10px] text-gray-500 bg-gray-50 px-1.5 py-0.5 rounded">{t}</span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              {data?.pain_signals?.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-[#0A1F44] mb-3 flex items-center gap-2">
                    <Clock className="h-4 w-4 text-red-400" /> Pain Signals
                  </h4>
                  <div className="space-y-1">
                    {data.pain_signals.map((s, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm text-red-700 bg-red-50/50 px-3 py-2 rounded-lg border border-red-100">
                        <Clock className="h-3.5 w-3.5 shrink-0" /> {s}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {data?.repeated_titles?.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-[#0A1F44] mb-3 flex items-center gap-2">
                    <Repeat className="h-4 w-4 text-violet-400" /> Repeated Titles (Hard to Fill)
                  </h4>
                  <div className="space-y-1">
                    {data.repeated_titles.map((r, i) => (
                      <div key={i} className="flex items-center justify-between text-sm px-3 py-2 bg-violet-50/50 rounded-lg border border-violet-100">
                        <span className="text-gray-700">{r.title}</span>
                        <span className="text-xs font-bold text-violet-700 bg-violet-100 px-2 py-0.5 rounded">{r.count}x</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {data?.outreach_angles?.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-[#0A1F44] mb-3 flex items-center gap-2">
                    <Target className="h-4 w-4 text-emerald-500" /> Outreach Angles
                  </h4>
                  <div className="space-y-1">
                    {data.outreach_angles.map((a, i) => (
                      <div key={i} className="flex items-start gap-2 text-sm text-gray-700 bg-emerald-50/50 px-3 py-2 rounded-lg border border-emerald-100">
                        <ArrowRight className="h-3.5 w-3.5 mt-0.5 text-emerald-500 shrink-0" /> {a}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {data?.commercial_case?.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-[#0A1F44] mb-3 flex items-center gap-2">
                    <Sparkles className="h-4 w-4 text-[#D4AF37]" /> Commercial Case
                  </h4>
                  <div className="space-y-1">
                    {data.commercial_case.map((c, i) => (
                      <div key={i} className="flex items-start gap-2 text-sm text-gray-700 bg-amber-50/50 px-3 py-2 rounded-lg border border-amber-100">
                        <Sparkles className="h-3.5 w-3.5 mt-0.5 text-[#D4AF37] shrink-0" /> {c}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {data?.niche_fits?.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-[#0A1F44] mb-3 flex items-center gap-2">
                    <Briefcase className="h-4 w-4 text-blue-400" /> Niche Fit
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {data.niche_fits.map((nf, i) => (
                      <div key={i} className="border border-blue-100 bg-blue-50/50 rounded-lg px-3 py-2 text-sm" data-testid={`niche-fit-${nf.niche}`}>
                        <span className="font-semibold text-blue-700">{nf.label}</span>
                        <span className="text-gray-500 ml-1">({nf.matching_roles} roles, score {nf.score}/10)</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {data?.locations?.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-[#0A1F44] mb-3 flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-gray-400" /> Hiring Locations
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {data.locations.map((l, i) => (
                      <span key={i} className="text-xs bg-gray-50 text-gray-600 px-2 py-1 rounded border border-gray-200">
                        {l.location} ({l.roles})
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/* ── Main Niche Lanes Page ── */
export default function NicheLanes({ embedded = false }) {
  const [rankingMode, setRankingMode] = useState('current'); // Phase 5A — default MUST stay 'current'
  const [activeNiche, setActiveNiche] = useState('all');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedRow, setExpandedRow] = useState(null);
  const [sortField, setSortField] = useState('total_score');
  const [sortDir, setSortDir] = useState('desc');
  const [similarTarget, setSimilarTarget] = useState(null);
  const [accountTarget, setAccountTarget] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showStrategic, setShowStrategic] = useState(false);
  const [fastWinsOnly, setFastWinsOnly] = useState(false);
  const [filterSize, setFilterSize] = useState('all');
  const [filterTier, setFilterTier] = useState('all');
  // New filters for "All Industries" mode
  const [filterIndustry, setFilterIndustry] = useState('all');
  const [filterState, setFilterState] = useState('all');
  const [filterNicheMatch, setFilterNicheMatch] = useState('all');
  const [filterConfidence, setFilterConfidence] = useState('all');
  // Pagination
  const [page, setPage] = useState(1);
  const [perPage] = useState(100);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  // Enrichment
  const [enriching, setEnriching] = useState(false);
  const [enrichResult, setEnrichResult] = useState(null);
  const [outreachTarget, setOutreachTarget] = useState(null);

  const loadData = useCallback(() => {
    setLoading(true);
    setExpandedRow(null);
    // When filtering to a specific tier, load more per page so search works
    // across the full filtered set (SE=177, BI=18, etc.)
    const effectivePerPage = filterTier !== 'all' ? 500 : perPage;
    const params = new URLSearchParams({
      limit: '10000', page: String(page), per_page: String(effectivePerPage),
    });
    if (filterTier !== 'all') {
      params.set('filter_tier', filterTier);
    }
    fetchWithAuth(`${API_URL}/api/intelligence/niche-lanes/${activeNiche}?${params}`)
      .then(d => {
        setData(d);
        setTotalPages(d?.total_pages || 1);
        setTotalCount(d?.total || 0);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [activeNiche, page, perPage, filterTier, showStrategic]);

  useEffect(() => { loadData(); }, [loadData]);

  const isAllMode = activeNiche === 'all';
  const activeNicheDef = NICHES.find(n => n.key === activeNiche);
  const companies = data?.companies || [];

  // Available filter options (derived from data)
  const industriesList = useMemo(() => data?.industries_represented || [], [data]);
  const statesList = useMemo(() => data?.states_represented || [], [data]);

  // Reset non-universal filters when switching niches
  const handleNicheChange = (key) => {
    setActiveNiche(key);
    setSearchQuery('');
    setFastWinsOnly(false);
    setFilterSize('all');
    setFilterTier('all');
    setFilterIndustry('all');
    setFilterState('all');
    setFilterNicheMatch('all');
    setFilterConfidence('all');
    setPage(1);
  };

  const handleEnrich = async () => {
    setEnriching(true);
    setEnrichResult(null);
    try {
      const r = await fetchWithAuth(`${API_URL}/api/intelligence/enrichment-priorities/smart-batch-enrich?max_count=50`, { method: 'POST' });
      setEnrichResult(r);
      loadData(); // Reload data after enrichment
    } catch (e) {
      setEnrichResult({ error: 'Enrichment failed' });
    } finally {
      setEnriching(false);
    }
  };

  // Filter pipeline — tier filter handled by backend, these are client-side refinements
  let visible = companies;
  if (fastWinsOnly) visible = visible.filter(c => c.priority_tier === 'best_immediate');
  if (filterSize !== 'all') visible = visible.filter(c => c.size_band === filterSize);
  if (filterIndustry !== 'all') visible = visible.filter(c => (c.industry || '').toLowerCase().includes(filterIndustry.toLowerCase()));
  if (filterState !== 'all') visible = visible.filter(c => (c.states || c.locations || []).some(s => typeof s === 'string' && s.includes(filterState)));
  if (filterNicheMatch === 'in_niche') visible = visible.filter(c => c.in_niche_lane || (c.niche_hits && c.niche_hits.length > 0));
  if (filterNicheMatch === 'broad') visible = visible.filter(c => !c.in_niche_lane && (!c.niche_hits || c.niche_hits.length === 0));
  if (filterConfidence !== 'all') visible = visible.filter(c => c.firmographic_confidence === filterConfidence);
  if (searchQuery) visible = visible.filter(c => c.company.toLowerCase().includes(searchQuery.toLowerCase()));

  const targetCompanies = companies.filter(c => c.qualification_status !== 'strategic_enterprise');
  const strategicCompanies = companies.filter(c => c.qualification_status === 'strategic_enterprise');
  const lowConfCount = companies.filter(c => c.firmographic_confidence === 'low').length;

  // Use full-pool summaries if available (not just current page)
  const tierSummary = data?.tier_summary || {};
  const sizeSummary = data?.size_summary || {};
  const confSummary = data?.confidence_summary || {};
  const hasPoolSummary = Object.keys(tierSummary).length > 0;
  const filtered = visible;

  const sorted = [...filtered].sort((a, b) => {
    const mul = sortDir === 'desc' ? -1 : 1;
    if (sortField === 'company') return mul * a.company.localeCompare(b.company);
    return mul * ((a[sortField] || 0) - (b[sortField] || 0));
  });

  const handleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    else { setSortField(field); setSortDir('desc'); }
  };

  const SortHeader = ({ field, label, align = 'left' }) => (
    <th className={`text-${align} px-4 py-3 cursor-pointer hover:text-[#0A1F44] select-none whitespace-nowrap`} onClick={() => handleSort(field)}>
      <span className="inline-flex items-center gap-1">
        {label}
        {sortField === field && (sortDir === 'desc' ? <ChevronDown className="h-3 w-3" /> : <ChevronUp className="h-3 w-3" />)}
      </span>
    </th>
  );

  // Count active filters
  const activeFilterCount = [
    filterIndustry !== 'all',
    filterState !== 'all',
    filterNicheMatch !== 'all',
    filterConfidence !== 'all',
    filterSize !== 'all',
    filterTier !== 'all',
    fastWinsOnly,
  ].filter(Boolean).length;

  return (
    <IntelligenceLayout title="Niche Lanes" embedded={embedded}>
      <header className="bg-white border-b border-gray-200 px-8 py-5 shadow-sm">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 bg-[#0A1F44]/5 rounded-xl flex items-center justify-center">
            <Briefcase className="h-5 w-5 text-[#0A1F44]" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-[#0A1F44]" data-testid="niche-lanes-title">
              {isAllMode ? 'All Qualified Targets' : 'Niche Lane Intelligence'}
            </h1>
            <p className="text-sm text-gray-500">
              {isAllMode
                ? 'Best qualified client targets across all targetable industries — ranked by commercial usefulness'
                : 'Qualified opportunities by recruiting specialty — powered by shared qualification pipeline'}
            </p>
          </div>
        </div>
      </header>

      <div className="px-8 pt-4 bg-white border-b border-gray-100" data-testid="ranking-mode-toggle">
        <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1 w-fit mb-4">
          <button
            onClick={() => setRankingMode('current')}
            data-testid="ranking-mode-current"
            className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${rankingMode === 'current' ? 'bg-white text-[#0A1F44] shadow-sm' : 'text-gray-500 hover:text-[#0A1F44]'}`}
          >
            Current Ranking
          </button>
          <button
            onClick={() => setRankingMode('canonical')}
            data-testid="ranking-mode-canonical"
            className={`flex items-center gap-1.5 px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${rankingMode === 'canonical' ? 'bg-[#0A1F44] text-white shadow-sm' : 'text-gray-500 hover:text-[#0A1F44]'}`}
          >
            <Sparkles className="h-3.5 w-3.5" /> Canonical Preview (Internal)
          </button>
        </div>
      </div>

      {rankingMode === 'canonical' ? (
        <CanonicalPreview />
      ) : (
      <main className="p-8 space-y-6">
        {/* Niche Tabs */}
        <div className="flex items-center gap-2" data-testid="niche-tabs">
          {NICHES.map(n => {
            const Icon = n.icon;
            const isActive = activeNiche === n.key;
            return (
              <button key={n.key} onClick={() => handleNicheChange(n.key)}
                data-testid={`niche-tab-${n.key}`}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all ${
                  isActive
                    ? 'bg-[#0A1F44] text-white shadow-md'
                    : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50 hover:border-gray-300'
                }`}>
                <Icon className="h-4 w-4" />
                {n.label}
              </button>
            );
          })}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20 gap-2 text-gray-400">
            <Loader2 className="h-5 w-5 animate-spin" /> Loading {activeNicheDef?.label} opportunities...
          </div>
        ) : (
          <>
            {/* KPI Cards — Full Pool Counts (click to filter) */}
            <div className={`grid grid-cols-2 ${isAllMode ? 'md:grid-cols-7' : 'md:grid-cols-5'} gap-4`} data-testid="niche-metrics">
              <div className="cursor-pointer" onClick={() => { setFilterTier('all'); setFastWinsOnly(false); setPage(1); }}>
                <MetricCard label="Total Qualified" value={totalCount} icon={Target} subtext="Full pool — all tiers" />
              </div>
              <div className={`cursor-pointer rounded-xl ring-2 ring-offset-1 transition-all ${filterTier === 'best_immediate' || fastWinsOnly ? 'ring-emerald-400' : 'ring-transparent'}`}
                onClick={() => { setFilterTier('best_immediate'); setFastWinsOnly(false); setPage(1); }}>
                <MetricCard label="Best Immediate" value={hasPoolSummary ? (tierSummary.best_immediate || 0) : targetCompanies.filter(c => c.priority_tier === 'best_immediate').length} icon={Zap} subtext="Ready to pursue now" />
              </div>
              <div className={`cursor-pointer rounded-xl ring-2 ring-offset-1 transition-all ${filterTier === 'needs_enrichment' ? 'ring-amber-400' : 'ring-transparent'}`}
                onClick={() => { setFilterTier('needs_enrichment'); setFastWinsOnly(false); setPage(1); }}>
                <MetricCard label="Needs Enrichment" value={hasPoolSummary ? (tierSummary.needs_enrichment || 0) : targetCompanies.filter(c => c.priority_tier === 'needs_enrichment').length} icon={Database} subtext="Missing or bad data" />
              </div>
              <div className={`cursor-pointer rounded-xl ring-2 ring-offset-1 transition-all ${filterTier === 'broader_qualified' ? 'ring-blue-400' : 'ring-transparent'}`}
                onClick={() => { setFilterTier('broader_qualified'); setFastWinsOnly(false); setPage(1); }}>
                <MetricCard label="Broader Qualified" value={hasPoolSummary ? (tierSummary.broader_qualified || 0) : targetCompanies.filter(c => c.priority_tier === 'broader_qualified').length} icon={Globe} subtext="Usable mid-priority targets" />
              </div>
              <div className={`cursor-pointer rounded-xl ring-2 ring-offset-1 transition-all ${filterTier === 'strategic_enterprise' ? 'ring-indigo-400' : 'ring-transparent'}`}
                onClick={() => {
                  if (filterTier === 'strategic_enterprise') {
                    setFilterTier('all'); setShowStrategic(false); setPage(1);
                  } else {
                    setFilterTier('strategic_enterprise'); setShowStrategic(true); setFastWinsOnly(false); setPage(1);
                  }
                }}>
                <MetricCard label="Strategic Enterprise" value={hasPoolSummary ? (tierSummary.strategic_enterprise || 0) : strategicCompanies.length} icon={Building2} subtext="Long-term / harder-access" />
              </div>
              {isAllMode && (
                <>
                  <MetricCard label="Mid-Market" value={hasPoolSummary ? (sizeSummary.mid_market || 0) : targetCompanies.filter(c => c.size_band === 'mid_market').length} icon={Users} subtext="50-1K employees" />
                  <MetricCard label="Low Confidence" value={hasPoolSummary ? (confSummary.low || 0) : lowConfCount} icon={AlertTriangle} subtext="Data mismatch flagged" />
                </>
              )}
            </div>
            {/* Pool scope label */}
            <p className="text-[10px] text-gray-400 -mt-3 px-1" data-testid="pool-scope-label">
              Card counts reflect the <strong>entire qualified pool</strong> (all pages). Table below shows the current page of {perPage}.
            </p>

            {/* Controls Bar */}
            <div className="bg-white border border-gray-200 rounded-xl shadow-sm" data-testid="controls-bar">
              {/* Primary controls row */}
              <div className="flex flex-wrap items-center gap-3 px-5 py-3">
                {/* Fast Wins — only meaningful for Best Immediate, auto-disabled otherwise */}
                {(filterTier === 'all' || filterTier === 'best_immediate') && (
                  <button onClick={() => {
                    const next = !fastWinsOnly;
                    setFastWinsOnly(next);
                    if (next) { setFilterTier('best_immediate'); setPage(1); }
                    else if (filterTier === 'best_immediate') { setFilterTier('all'); setPage(1); }
                  }}
                    data-testid="fast-wins-btn"
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                      fastWinsOnly ? 'bg-emerald-500 text-white shadow' : 'bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100'
                    }`}>
                    <Zap className="h-3.5 w-3.5" />
                    {fastWinsOnly ? 'Showing Fast Wins Only' : 'Fast Wins'}
                  </button>
                )}

                {/* Tier filter — drives backend filter_tier */}
                <select value={filterTier} onChange={e => {
                  const val = e.target.value;
                  setFilterTier(val);
                  setFastWinsOnly(false);
                  setPage(1);
                  setShowStrategic(val === 'strategic_enterprise');
                }}
                  data-testid="filter-tier"
                  className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 text-gray-600 focus:outline-none focus:border-[#D4AF37]">
                  <option value="all">All Tiers</option>
                  <option value="best_immediate">Best Immediate</option>
                  <option value="needs_enrichment">Needs Enrichment</option>
                  <option value="broader_qualified">Broader Qualified</option>
                  <option value="strategic_enterprise">Strategic Enterprise</option>
                </select>

                {/* Size filter */}
                <select value={filterSize} onChange={e => setFilterSize(e.target.value)}
                  data-testid="filter-size"
                  className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 text-gray-600 focus:outline-none focus:border-[#D4AF37]">
                  <option value="all">All Sizes</option>
                  <option value="mid_market">Mid-Market</option>
                  <option value="growth">Growth</option>
                  <option value="unknown">Size Unknown</option>
                  <option value="small">Small</option>
                </select>

                {/* Confidence filter (always visible now) */}
                <select value={filterConfidence} onChange={e => setFilterConfidence(e.target.value)}
                  data-testid="filter-confidence"
                  className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 text-gray-600 focus:outline-none focus:border-[#D4AF37]">
                  <option value="all">All Confidence</option>
                  <option value="high">High Confidence</option>
                  <option value="medium">Medium Confidence</option>
                  <option value="low">Low Confidence</option>
                </select>

                {/* Industry filter (All Industries mode) */}
                {isAllMode && industriesList.length > 0 && (
                  <select value={filterIndustry} onChange={e => setFilterIndustry(e.target.value)}
                    data-testid="filter-industry"
                    className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 text-gray-600 focus:outline-none focus:border-[#D4AF37] max-w-[180px]">
                    <option value="all">All Industries</option>
                    {industriesList.map(ind => (
                      <option key={ind} value={ind}>{ind}</option>
                    ))}
                  </select>
                )}

                {/* State filter (All Industries mode) */}
                {isAllMode && statesList.length > 0 && (
                  <select value={filterState} onChange={e => setFilterState(e.target.value)}
                    data-testid="filter-state"
                    className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 text-gray-600 focus:outline-none focus:border-[#D4AF37] max-w-[140px]">
                    <option value="all">All States</option>
                    {statesList.map(st => (
                      <option key={st} value={st}>{st}</option>
                    ))}
                  </select>
                )}

                {/* Niche Lane Match filter (All Industries mode) */}
                {isAllMode && (
                  <select value={filterNicheMatch} onChange={e => setFilterNicheMatch(e.target.value)}
                    data-testid="filter-niche-match"
                    className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 text-gray-600 focus:outline-none focus:border-[#D4AF37]">
                    <option value="all">All Targets</option>
                    <option value="in_niche">In Niche Lane</option>
                    <option value="broad">Broad Targets Only</option>
                  </select>
                )}

                {/* Search */}
                <div className="relative flex-1 min-w-[200px]">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                  <input value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search companies..."
                    data-testid="niche-search"
                    className="w-full pl-8 pr-4 py-1.5 rounded-lg border border-gray-200 text-xs focus:outline-none focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37]/30" />
                </div>

                {/* Strategic toggle — shows/hides long-term harder-access accounts */}
                <label className="flex items-center gap-2 cursor-pointer ml-auto" data-testid="strategic-toggle">
                  <span className="text-xs text-gray-500">{showStrategic ? 'Viewing Strategic Enterprise' : 'Show long-term accounts'}</span>
                  <div className={`relative w-9 h-5 rounded-full transition-colors ${showStrategic ? 'bg-indigo-500' : 'bg-gray-300'}`}
                    onClick={() => {
                      const next = !showStrategic;
                      setShowStrategic(next);
                      if (next && filterTier === 'all') {
                        setFilterTier('strategic_enterprise');
                        setFastWinsOnly(false);
                        setPage(1);
                      } else if (!next && filterTier === 'strategic_enterprise') {
                        setFilterTier('all');
                        setPage(1);
                      }
                    }}>
                    <div className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform ${showStrategic ? 'translate-x-4' : 'translate-x-0.5'}`} />
                  </div>
                </label>

                {/* Count + active filters */}
                <span className="text-xs text-gray-400" data-testid="filter-count">
                  {filtered.length} on this page{activeFilterCount > 0 ? ` | ${activeFilterCount} filter${activeFilterCount > 1 ? 's' : ''}` : ''}
                  {filterTier !== 'all' ? ` | Tier: ${filterTier.replace('_', ' ')}` : ''}
                </span>
              </div>
            </div>

            {/* Outreach angles & buyer themes */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {data?.outreach_angles?.length > 0 && (
                <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm" data-testid="outreach-angles">
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Outreach Angles for {activeNicheDef?.label}</h3>
                  <div className="space-y-2">
                    {data.outreach_angles.map((a, i) => (
                      <div key={i} className="flex items-start gap-2 text-sm text-gray-700">
                        <ArrowRight className="h-3.5 w-3.5 mt-0.5 text-emerald-500 shrink-0" /> {a}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {data?.buyer_themes?.length > 0 && (
                <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm" data-testid="buyer-themes">
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Buyer Themes</h3>
                  <div className="space-y-2">
                    {data.buyer_themes.map((t, i) => (
                      <div key={i} className="flex items-start gap-2 text-sm text-gray-700">
                        <Sparkles className="h-3.5 w-3.5 mt-0.5 text-[#D4AF37] shrink-0" /> {t}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Company Table */}
            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm" data-testid="niche-table">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-xs text-gray-500 uppercase border-b border-gray-100 bg-gray-50">
                      <SortHeader field="company" label="Company" />
                      <th className="text-left px-4 py-3">Tier</th>
                      <SortHeader field="total_score" label="Score" align="center" />
                      <SortHeader field="outreach_fit" label="Fit" align="center" />
                      <SortHeader field="total_postings" label="Roles" align="right" />
                      <th className="text-left px-4 py-3">Indicators</th>
                      <th className="text-left px-4 py-3">Why</th>
                      <th className="text-center px-4 py-3">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sorted.length === 0 ? (
                      <tr><td colSpan={8} className="text-center py-12 text-gray-400">No qualified companies found{activeNiche !== 'all' ? ' for this niche' : ''}.</td></tr>
                    ) : sorted.map((item, i) => {
                      const isSE = item.qualification_status === 'strategic_enterprise';
                      const hasLowConf = item.firmographic_confidence === 'low';
                      return (
                      <React.Fragment key={item.company}>
                        <tr className={`border-t border-gray-100 transition-colors cursor-pointer ${
                          hasLowConf ? 'bg-red-50/30 hover:bg-red-50/60' :
                          isSE ? 'bg-indigo-50/40 hover:bg-indigo-50/70' : 'hover:bg-[#fdf8ef]'
                        }`}
                          onClick={() => setExpandedRow(expandedRow === i ? null : i)} data-testid={`niche-row-${i}`}>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <WatchlistButton company={item.company} sourceModule="niche-lanes" />
                              <div>
                                <div className="font-medium text-[#0A1F44]">{item.company}</div>
                                <div className="text-[10px] text-gray-400">
                                  {item.industry || '—'}
                                  {item.employee_count > 0 ? ` | ${item.employee_count.toLocaleString()} emp` : ''}
                                  {item.best_niche && isAllMode ? ` | ${item.best_niche}` : ''}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3"><TierBadge tier={item.priority_tier} /></td>
                          <td className="px-4 py-3 text-center">
                            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-sm font-bold bg-[#0A1F44]/5 text-[#0A1F44] border border-[#0A1F44]/10">
                              {item.total_score}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className={`inline-flex px-2 py-0.5 rounded text-xs font-bold ${item.outreach_fit >= 4 ? 'bg-emerald-50 text-emerald-700' : item.outreach_fit >= 2 ? 'bg-blue-50 text-blue-700' : 'bg-gray-100 text-gray-500'}`}>
                              {item.outreach_fit}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right font-semibold text-[#0A1F44]">{item.total_postings}</td>
                          <td className="px-4 py-3">
                            <div className="flex flex-wrap gap-1">
                              {item.tags?.slice(0, 2).map(t => <QualTag key={t} tag={t} />)}
                              <ConfidenceBadge confidence={item.firmographic_confidence} flags={item.confidence_flags} />
                              <EnrichmentBadge ep={item.enrichment_priority} />
                              {isAllMode && <NicheLaneBadge inNiche={item.in_niche_lane} nicheHits={item.niche_hits} />}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-xs text-gray-600 line-clamp-2">{item.why_text}</span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <div className="flex items-center justify-center gap-1">
                              <button onClick={e => { e.stopPropagation(); setSimilarTarget(item.company); }}
                                data-testid={`find-similar-${i}`}
                                className="p-1.5 rounded-lg text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition-colors" title="Find More Like This">
                                <Search className="h-3.5 w-3.5" />
                              </button>
                              <button onClick={e => { e.stopPropagation(); setAccountTarget(item.company); }}
                                data-testid={`account-expand-${i}`}
                                className="p-1.5 rounded-lg text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 transition-colors" title="Account Expansion">
                                <Building2 className="h-3.5 w-3.5" />
                              </button>
                              <CompanyActionMenu
                                companyName={item.company}
                                companyData={{ industry: item.industry, employee_count: item.employee_count }}
                                onOutreach={(c) => setOutreachTarget(c)}
                              />
                            </div>
                          </td>
                        </tr>
                        {expandedRow === i && (
                          <tr className={hasLowConf ? 'bg-red-50/20' : 'bg-[#fdf8ef]'}>
                            <td colSpan={8} className="px-6 py-4">
                              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                {/* Confidence Flags (if any) */}
                                {item.confidence_flags?.length > 0 && (
                                  <div className="md:col-span-4 mb-2">
                                    <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 border border-red-200">
                                      <AlertTriangle className="h-4 w-4 text-red-500 shrink-0 mt-0.5" />
                                      <div>
                                        <p className="text-xs font-semibold text-red-700 mb-1">Firmographic Confidence: {item.firmographic_confidence?.toUpperCase()}</p>
                                        <ul className="text-xs text-red-600 space-y-0.5">
                                          {item.confidence_flags.map((f, j) => (
                                            <li key={j}>- {f}</li>
                                          ))}
                                        </ul>
                                        <p className="text-[10px] text-red-500 mt-1">This company was downranked from Best Immediate due to data mismatch</p>
                                      </div>
                                    </div>
                                  </div>
                                )}
                                <div>
                                  <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">Matched Niche Roles</h4>
                                  <div className="space-y-1">
                                    {item.matched_roles?.length > 0 ? item.matched_roles.map((r, j) => (
                                      <span key={j} className="inline-block text-[10px] bg-blue-50 text-blue-600 px-2 py-0.5 rounded border border-blue-100 mr-1 mb-1">{r}</span>
                                    )) : <span className="text-xs text-gray-400">None</span>}
                                  </div>
                                </div>
                                <div>
                                  <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">Repeated Titles</h4>
                                  {item.repeated_niche_titles?.length > 0 ? item.repeated_niche_titles.map((r, j) => (
                                    <div key={j} className="flex items-center justify-between text-xs mb-1">
                                      <span className="text-gray-700 truncate mr-2">{r.title}</span>
                                      <span className="text-red-600 font-bold bg-red-50 px-1.5 py-0.5 rounded">{r.count}x</span>
                                    </div>
                                  )) : <span className="text-xs text-gray-400">None</span>}
                                </div>
                                <div>
                                  <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">Hiring Signals</h4>
                                  <div className="space-y-1 text-xs">
                                    <div className="flex items-center gap-1 text-gray-600">
                                      <TrendingUp className="h-3 w-3 text-blue-400" /> 30d velocity: {item.hiring_velocity} roles
                                    </div>
                                    <div className="flex items-center gap-1 text-gray-600">
                                      <Clock className={`h-3 w-3 ${item.avg_age_days > 30 ? 'text-red-400' : 'text-gray-400'}`} /> Avg age: {item.avg_age_days}d
                                    </div>
                                    <div className="flex items-center gap-1 text-gray-600">
                                      <Target className="h-3 w-3 text-gray-400" /> Niche ratio: {Math.round((item.niche_role_ratio || 0) * 100)}%
                                    </div>
                                    {item.firmographic_confidence && (
                                      <div className={`flex items-center gap-1 ${item.firmographic_confidence === 'low' ? 'text-red-600' : item.firmographic_confidence === 'medium' ? 'text-amber-600' : 'text-emerald-600'}`}>
                                        {item.firmographic_confidence === 'high'
                                          ? <CheckCircle2 className="h-3 w-3" />
                                          : <AlertTriangle className="h-3 w-3" />}
                                        Data confidence: {item.firmographic_confidence}
                                      </div>
                                    )}
                                  </div>
                                </div>
                                <div>
                                  <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">Locations</h4>
                                  {item.locations?.length > 0 ? item.locations.map((l, j) => (
                                    <div key={j} className="flex items-center gap-1 text-xs text-gray-600 mb-0.5">
                                      <MapPin className="h-3 w-3 text-gray-400" /> {l}
                                    </div>
                                  )) : <span className="text-xs text-gray-400">—</span>}
                                </div>
                              </div>
                              {/* Niche hits for All Industries mode */}
                              {isAllMode && item.niche_hits?.length > 0 && (
                                <div className="mt-3 pt-3 border-t border-gray-200">
                                  <span className="text-xs font-semibold text-gray-500 uppercase">Niche Lane Matches: </span>
                                  <span className="text-xs text-gray-600">
                                    {item.niche_hits.map(h => `${h.label} (score ${h.score})`).join(' | ')}
                                  </span>
                                </div>
                              )}
                              {item.qualification_status === 'manual_override' && (
                                <div className="mt-3 pt-3 border-t border-violet-200">
                                  <span className="text-xs text-violet-600 font-semibold">Manual Override Active</span>
                                </div>
                              )}
                              {item.qualification_status === 'strategic_enterprise' && (
                                <div className="mt-3 pt-3 border-t border-indigo-200">
                                  <span className="text-xs text-indigo-600 font-semibold">Strategic Enterprise — Long-term / Harder Access</span>
                                  {item.strategic_enterprise_reasons?.length > 0 && (
                                    <span className="text-xs text-gray-500 ml-2">— {item.strategic_enterprise_reasons.slice(0, 2).join(' | ')}</span>
                                  )}
                                </div>
                              )}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="bg-white border border-gray-200 rounded-xl px-5 py-3 shadow-sm flex items-center justify-between" data-testid="pagination-controls">
                <span className="text-xs text-gray-500">
                  Page {page} of {totalPages} ({data?.filtered_count ? `${data.filtered_count.toLocaleString()} in this view` : `${totalCount.toLocaleString()} total`})
                </span>
                <div className="flex items-center gap-2">
                  <button disabled={page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))}
                    data-testid="pagination-prev"
                    className="px-3 py-1.5 rounded-lg text-xs font-medium border border-gray-200 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                    Previous
                  </button>
                  {[...Array(Math.min(totalPages, 7))].map((_, i) => {
                    let pageNum;
                    if (totalPages <= 7) pageNum = i + 1;
                    else if (page <= 4) pageNum = i + 1;
                    else if (page >= totalPages - 3) pageNum = totalPages - 6 + i;
                    else pageNum = page - 3 + i;
                    return (
                      <button key={pageNum} onClick={() => setPage(pageNum)}
                        className={`px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                          page === pageNum ? 'bg-[#0A1F44] text-white' : 'border border-gray-200 hover:bg-gray-50 text-gray-600'
                        }`}>
                        {pageNum}
                      </button>
                    );
                  })}
                  <button disabled={page >= totalPages} onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    data-testid="pagination-next"
                    className="px-3 py-1.5 rounded-lg text-xs font-medium border border-gray-200 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                    Next
                  </button>
                </div>
              </div>
            )}

            {/* Enrichment Actions */}
            {isAllMode && (
              <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm" data-testid="enrichment-actions">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-sm font-semibold text-[#0A1F44] flex items-center gap-2">
                      <Database className="h-4 w-4 text-amber-500" /> Enrichment Actions
                    </h3>
                    <p className="text-xs text-gray-500 mt-1">
                      {totalCount.toLocaleString()} qualified targets found. Many have unknown size and need enrichment to unlock Best Immediate tier.
                    </p>
                  </div>
                  <button onClick={handleEnrich} disabled={enriching}
                    data-testid="enrich-btn"
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                      enriching ? 'bg-amber-100 text-amber-700 cursor-wait' : 'bg-amber-500 text-white hover:bg-amber-600 shadow-sm'
                    }`}>
                    {enriching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Zap className="h-4 w-4" />}
                    {enriching ? 'Enriching...' : 'Enrich Top 50 Companies'}
                  </button>
                </div>
                {enrichResult && !enrichResult.error && (
                  <div className="mt-3 pt-3 border-t border-gray-100" data-testid="enrich-result">
                    <div className="grid grid-cols-5 gap-3 text-center">
                      <div className="bg-emerald-50 rounded-lg p-2">
                        <p className="text-lg font-bold text-emerald-700">{enrichResult.impact?.became_mid_market || 0}</p>
                        <p className="text-[10px] text-emerald-600">New Mid-Market</p>
                      </div>
                      <div className="bg-blue-50 rounded-lg p-2">
                        <p className="text-lg font-bold text-blue-700">{enrichResult.impact?.became_growth || 0}</p>
                        <p className="text-[10px] text-blue-600">New Growth</p>
                      </div>
                      <div className="bg-indigo-50 rounded-lg p-2">
                        <p className="text-lg font-bold text-indigo-700">{enrichResult.impact?.became_enterprise || 0}</p>
                        <p className="text-[10px] text-indigo-600">Enterprise</p>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-2">
                        <p className="text-lg font-bold text-gray-700">{enrichResult.impact?.no_data || 0}</p>
                        <p className="text-[10px] text-gray-600">No Data</p>
                      </div>
                      <div className="bg-amber-50 rounded-lg p-2">
                        <p className="text-lg font-bold text-amber-700">{enrichResult.attempted || 0}</p>
                        <p className="text-[10px] text-amber-600">Total Enriched</p>
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                      {enrichResult.remaining_candidates?.toLocaleString() || 0} companies still awaiting enrichment
                    </p>
                  </div>
                )}
                {enrichResult?.error && (
                  <p className="mt-2 text-xs text-red-600">{enrichResult.error}</p>
                )}
              </div>
            )}

            {/* Info Footer */}
            <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
              <div className="flex items-start gap-2 text-xs text-gray-500">
                <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                <p>
                  {isAllMode
                    ? <>
                        <strong>How this view works:</strong> Card counts always reflect the <strong>entire qualified pool</strong> across all pages. Click a card to filter by that tier. The table shows {perPage} companies per page, sorted by commercial priority within the selected tier.
                        <br /><br />
                        <strong>Best Immediate</strong> — confirmed mid-market or strong growth, high confidence, ready to pursue.{' '}
                        <strong>Broader Qualified</strong> — passed all exclusions, commercially realistic, but needs more signal or enrichment to be top-tier.{' '}
                        <strong>Needs Enrichment</strong> — promising targets with missing/unreliable firmographic data.{' '}
                        <strong>Strategic Enterprise</strong> — large/valuable companies, hidden by default. Long-term opportunities that are harder to access but worth tracking.
                      </>
                    : <>
                        Niche Lanes run on top of the shared qualification-first pipeline. <strong>Targets</strong> are mid-market and growth companies that passed all exclusion filters. <strong>Strategic Enterprise</strong> companies are enterprise/oversized accounts with real strategic value — click the card to view them. Companies with <strong className="text-red-600">Low Confidence</strong> data are downranked to prevent false positives from bad enrichment.
                      </>
                  }
                </p>
              </div>
            </div>
          </>
        )}
      </main>
      )}

      {/* Panels */}
      {similarTarget && <SimilarPanel company={similarTarget} niche={activeNiche} onClose={() => setSimilarTarget(null)} />}
      {accountTarget && <AccountPanel company={accountTarget} onClose={() => setAccountTarget(null)} />}
      {outreachTarget && <QuickOutreachModal company={outreachTarget} onClose={() => setOutreachTarget(null)} onSent={() => setOutreachTarget(null)} />}
    </IntelligenceLayout>
  );
}
